import {
  EventColorCategory,
  ICalendarColorsMap,
} from "@clockwise/client-commons/src/util/event-category-coloring";
import {
  ConferencingType,
  EventPermissions,
  ResponseStatusEnum,
  TransparencyEnum,
} from "@clockwise/schema";
import {
  ConferencingType as GatewayConferencingType,
  ResponseStatusEnum as GatewayResponseStatusEnum,
} from "@clockwise/schema/v2";
import { getEventStartEndTimeText } from "@clockwise/web-commons/src/components/calendar/calendar-event/utils/getEventStartEndTimeText";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { Interval } from "luxon";
import { CalendarEventsQuery } from "../apollo/__generated__/CalendarEvents.v2.generated";
import { EventPermissions as EventPermissionsBooleans, PlannerEventCardsByDay } from "../types";

// TODO (lsanwick) Remove this after converting to use Gateway types.
const toConferencingType = (gatewayType: GatewayConferencingType): ConferencingType => {
  switch (gatewayType) {
    case GatewayConferencingType.Meet:
      return ConferencingType.MEET;
    case GatewayConferencingType.TeamsForBusiness:
      return ConferencingType.TEAMS_FOR_BUSINESS;
    case GatewayConferencingType.Zoom:
      return ConferencingType.ZOOM;
    case GatewayConferencingType.Other:
      return ConferencingType.OTHER;
  }
};

// TODO (lsanwick) Use this to convert the old event permissions to the new capability booleans,
// after migration.
export const toEventPermissions = (enumValue: EventPermissions): EventPermissionsBooleans => {
  switch (enumValue) {
    case EventPermissions.ALL:
      return {
        canRSVP: true,
        canSeeOtherGuests: true,
        canInviteOthers: true,
        canModify: true,
        canDelete: true,
        canRemove: true,
      };
    case EventPermissions.MODIFY:
      return {
        canRSVP: true,
        canSeeOtherGuests: true,
        canInviteOthers: true,
        canModify: true,
        canDelete: false,
        canRemove: true,
      };
    case EventPermissions.INVITE:
      return {
        canRSVP: true,
        canSeeOtherGuests: true,
        canInviteOthers: true,
        canModify: false,
        canDelete: false,
        canRemove: true,
      };
    case EventPermissions.ISATTENDEE:
      return {
        canRSVP: true,
        canSeeOtherGuests: true,
        canInviteOthers: false,
        canModify: false,
        canDelete: false,
        canRemove: true,
      };
    case EventPermissions.NONE:
      return {
        canRSVP: false,
        canSeeOtherGuests: false,
        canInviteOthers: false,
        canModify: false,
        canDelete: false,
        canRemove: false,
      };
  }
};

function toWebserverResponseStatus(
  gatewayResponseStatus: GatewayResponseStatusEnum,
): ResponseStatusEnum {
  switch (gatewayResponseStatus) {
    case GatewayResponseStatusEnum.Accepted:
      return ResponseStatusEnum.Accepted;
    case GatewayResponseStatusEnum.Declined:
      return ResponseStatusEnum.Declined;
    case GatewayResponseStatusEnum.Tentative:
      return ResponseStatusEnum.Tentative;
    case GatewayResponseStatusEnum.NeedsAction:
      return ResponseStatusEnum.NeedsAction;
    case GatewayResponseStatusEnum.NotApplicable:
      return ResponseStatusEnum.NotApplicable;
  }
}

export function toPlannerEvents(
  data?: CalendarEventsQuery,
  colorMap?: ICalendarColorsMap,
): PlannerEventCardsByDay {
  return (
    data?.calendars.reduce<PlannerEventCardsByDay>((acc, member) => {
      member?.eventsByWeek.forEach((event) => {
        const range =
          event.dateOrTimeRange.__typename === "DateTimeRange"
            ? event.dateOrTimeRange.timeRange
            : event.dateOrTimeRange.dateRange;
        const timeInterval = Interval.fromISO(range, {
          zone: getRenderTimeZone(),
        });
        const date = timeInterval.start.toISODate();

        acc[date] = [
          ...(acc[date] ?? []),
          {
            active: false,
            annotaion: undefined,
            attendeeCount: event.attendees.length,
            attendeesCalendarIds: event.attendees.map((a) => a.id),
            calendarIds: [event.calendarId],
            deemphasis: false,
            eventCategory: (event.category.type as unknown) as EventColorCategory,
            eventCategoryColor: event.category.color ? colorMap?.[event.category.color] : undefined,
            eventPermissions: event.eventPermissions,
            externalEventId: event.externalEventId,
            flexStatus: null,
            interval: timeInterval,
            isAllDay: event.isAllDay,
            isCancelled: event.isCanceled,
            key: event.id,
            locked: event.isLocked,
            responseState: toWebserverResponseStatus(event.responseStatus),
            subText: getEventStartEndTimeText(timeInterval.start, timeInterval.end),
            text: event.title,
            transparency: event.isMarkedBusy
              ? TransparencyEnum.Opaque
              : TransparencyEnum.Transparent,
            isCollapseable: event.isCollapsible,
            smartHold: event.smartHold
              ? {
                  holdType: event.smartHold.holdType,
                  state: event.smartHold.state,
                }
              : null,
            videoLink: event.conferencing
              ? {
                  __typename: "VideoLink",
                  uri: event.conferencing.url,
                  type: toConferencingType(event.conferencing.type),
                }
              : null,
          },
        ];
      });
      return acc;
    }, {}) ?? {}
  );
}
